import { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

import { ReactComponent as CloseIcon } from "../../../../assets/images/close.svg";
import UploadFile from "../../../../components/UploadFile";
import { useUploadImage } from "../services";
import { TAttribute, TCreateAttributeRequest, createAttributeSchema } from "../schemas";

type ImageData = {
  id: Date;
  name: string;
  displayName: string;
  size: string;
  image: string;
  upload: boolean;
};

type AttributeFormDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TCreateAttributeRequest) => void;
  editAttribute?: TAttribute | null;
  isLoading?: boolean;
  themeMode?: string;
};

const AttributeFormDialog = ({
  open,
  onClose,
  onSubmit,
  editAttribute,
  isLoading = false,
  themeMode = "dark",
}: AttributeFormDialogProps) => {
  const [iconUrl, setIconUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [iconList, setIconList] = useState<ImageData[]>([]);
  const [imageList, setImageList] = useState<ImageData[]>([]);
  const [iconLoading, setIconLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const uploadImageMutation = useUploadImage();

  const isEditMode = !!editAttribute;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TCreateAttributeRequest>({
    resolver: zodResolver(createAttributeSchema),
    defaultValues: {
      name: "",
      description: "",
      icon: "",
      image: "",
    },
  });

  const resetForm = useCallback(() => {
    reset({
      name: "",
      description: "",
      icon: "",
      image: "",
    });
    setIconUrl("");
    setImageUrl("");
    setIconList([]);
    setImageList([]);
  }, [reset]);

  // Reset form when dialog opens/closes or attribute changes
  useEffect(() => {
    if (open) {
      if (editAttribute) {
        reset({
          name: editAttribute.name,
          description: editAttribute.description || "",
          icon: editAttribute.icon || "",
          image: editAttribute.image || "",
        });
        setIconUrl(editAttribute.icon || "");
        setImageUrl(editAttribute.image || "");
        if (editAttribute.icon) {
          setIconList([
            {
              id: new Date(),
              name: "icon",
              displayName: "icon",
              size: "0",
              image: editAttribute.icon,
              upload: true,
            },
          ]);
        }
        if (editAttribute.image) {
          setImageList([
            {
              id: new Date(),
              name: "image",
              displayName: "image",
              size: "0",
              image: editAttribute.image,
              upload: true,
            },
          ]);
        }
      } else {
        resetForm();
      }
    }
  }, [open, editAttribute, reset, resetForm]);

  const handleIconUpload = (data: ImageData, file: File) => {
    setIconLoading(true);
    setIconList([data]);

    uploadImageMutation.mutate(file, {
      onSuccess: (response) => {
        if (response.success && response.data?.location) {
          setIconUrl(response.data.location);
          setValue("icon", response.data.location, { shouldValidate: true });
          setIconList([{ ...data, name: response.data.fileId, image: response.data.location, upload: true }]);
        } else {
          setIconList([]);
        }
        setIconLoading(false);
      },
      onError: (error) => {
        console.error("Icon upload failed:", error);
        setIconList([]);
        setIconLoading(false);
      },
    });
  };

  const handleImageUpload = (data: ImageData, file: File) => {
    setImageLoading(true);
    setImageList([data]);

    uploadImageMutation.mutate(file, {
      onSuccess: (response) => {
        if (response.success && response.data?.location) {
          setImageUrl(response.data.location);
          setImageList([{ ...data, name: response.data.fileId, image: response.data.location, upload: true }]);
        } else {
          setImageList([]);
        }
        setImageLoading(false);
      },
      onError: (error) => {
        console.error("Image upload failed:", error);
        setImageList([]);
        setImageLoading(false);
      },
    });
  };

  const handleRemoveIcon = () => {
    setIconList([]);
    setIconUrl("");
    setValue("icon", "", { shouldValidate: true });
  };

  const handleRemoveImage = () => {
    setImageList([]);
    setImageUrl("");
  };

  const onFormSubmit = (data: TCreateAttributeRequest) => {
    const formData: TCreateAttributeRequest = {
      name: data.name.trim(),
      description: data.description?.trim() || undefined,
      icon: iconUrl,
      image: imageUrl || undefined,
    };

    onSubmit(formData);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={styles.dialog}>
      <DialogTitle sx={styles.title}>
        <Typography sx={styles.titleText}>
          {isEditMode ? "Edit Attribute" : "Add Attribute"}
        </Typography>
        <CloseIcon style={{ cursor: "pointer" }} onClick={handleClose} />
      </DialogTitle>

      <DialogContent sx={styles.content}>
        <Box component="form" sx={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
                required
                sx={styles.textField}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                rows={3}
                sx={styles.textField}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={styles.uploadLabel}>Icon *</Typography>
              <Box sx={styles.uploadWrapper}>
                <UploadFile
                  uploadFile={handleIconUpload}
                  imageList={iconList}
                  onRemoveImage={handleRemoveIcon}
                  uploadError={(msg: string) => console.error(msg)}
                  maxFiles={1}
                  uploadText="Upload Icon"
                  imageLoading={iconLoading}
                  themeMode={themeMode}
                  page="addResItemImg"
                />
              </Box>
              {errors.icon && (
                <Typography sx={styles.errorText}>{errors.icon.message}</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={styles.uploadLabel}>Image</Typography>
              <Box sx={styles.uploadWrapper}>
                <UploadFile
                  uploadFile={handleImageUpload}
                  imageList={imageList}
                  onRemoveImage={handleRemoveImage}
                  uploadError={(msg: string) => console.error(msg)}
                  maxFiles={1}
                  uploadText="Upload Image"
                  imageLoading={imageLoading}
                  themeMode={themeMode}
                  page="addResItemImg"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={handleClose} sx={styles.cancelBtn} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onFormSubmit)}
          sx={styles.submitBtn}
          disabled={isLoading || iconLoading || imageLoading}
        >
          {isLoading ? "Saving..." : isEditMode ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttributeFormDialog;

const styles = {
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 3,
    },
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: "GilroyBold",
    fontSize: 18,
  },
  titleText: {
    fontFamily: "GilroyBold",
    fontSize: 18,
  },
  content: {
    padding: "24px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  label: {
    fontFamily: "GilroySemiBold",
    fontSize: 14,
    color: "#333",
  },
  textField: {
    "& .MuiInputBase-root": {
      fontFamily: "GilroyRegular",
      fontSize: 14,
    },
  },
  uploadLabel: {
    fontFamily: "GilroySemiBold",
    fontSize: 14,
    color: "#333",
    marginBottom: "8px",
  },
  uploadWrapper: {
    "& > .MuiGrid-root": {
      height: "150px !important",
      minHeight: "150px !important",
    },
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 12,
    fontFamily: "GilroyRegular",
    marginTop: 1,
  },
  actions: {
    padding: 2,
    gap: 1,
  },
  cancelBtn: {
    fontFamily: "GilroySemiBold",
    color: "#666",
    border: "1px solid #AAA",
    "&:hover": {
      borderColor: "#000",
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  },
  submitBtn: {
    fontFamily: "GilroySemiBold",
    backgroundColor: "#000",
    color: "#FFF",
    minWidth: 100,
    "&:hover": {
      backgroundColor: "#333",
    },
    "&:disabled": {
      backgroundColor: "#ccc",
      color: "#666",
    },
  },
};
